<template>
  <nav :class="{ open: isOpen }">
    <section v-for="(category, categoryIndex) in categories" :key="category.type">
      <h2 v-if="categoryIndex === 0" class="title commonHeadingLarge" @click="isOpen = !isOpen">
        {{ category.title }}
        <icon fas="fa-chevron-down" class="toggle-icon" :class="{ open: isOpen }"></icon>
      </h2>
      <h2 v-else class="title commonHeadingLarge">
        {{ category.title }}
      </h2>
      <ul class="category">
        <li v-for="regionSlugs in displayRegionSlugs" :key="toArray<string>(regionSlugs)[0]" class="region-list">
          <span class="region">
            <template v-for="regionSlug in toArray<string>(regionSlugs)" :key="regionSlug">
              <custom-nuxt-link
                :to="`/${regionBySlug(regionSlug)?.path.concat(category.suffixPath)}`"
                :class="{ disabledUrl: category.type === 'ranking_konkatsu' }"
                class="textBold"
              >
                {{ regionBySlug(regionSlug).name }}
              </custom-nuxt-link>
            </template>
          </span>
          <ul class="prefecture-list">
            <li v-for="pref in prefecturesByRegionSlug(regionSlugs)" :key="pref.slug" class="prefecture">
              <custom-nuxt-link
                :to="`${category.prefixPath ? `/${category.prefixPath.concat(pref.slug).concat(category.suffixPath)}` : `/${pref.path.concat(category.suffixPath)}`}`"
              >
                {{ pref.name }}
              </custom-nuxt-link>

              <ul v-if="category.type !== 'ranking_konkatsu'" class="areas-list">
                <li v-for="area in areasByPrefecture(pref.slug)" :key="area.name" class="area">
                  <custom-nuxt-link :to="`/${area.path.concat(category.suffixPath)}`">
                    {{ area.name }}
                  </custom-nuxt-link>
                </li>
              </ul>
            </li>
          </ul>
        </li>
      </ul>
    </section>
    <section>
      <h2 class="commonHeadingLarge title">イベントジャンルから街コン（全イベント）を探す</h2>
      <ul class="categories-list">
        <li v-for="category in eventCategoryLinks" :key="category.name">
          <custom-nuxt-link :to="category.url">{{ category.name }}</custom-nuxt-link>
        </li>
      </ul>
    </section>
  </nav>
</template>

<script setup lang="ts">
import { useRoute } from 'vue-router'
import { listRegions, getListPrefectureByRegionSlug, findAreaBySlug } from '@/models/places'
import { useMainStore } from '@/store'
import { addPathToListPlace, addPathToPlace } from '@/utils/formatPlace'
import PageDetector from '@/utils/PageDetector'

const DISPLAY_AREAS = {
  hokkaido: {
    name: '札幌',
    path: 'areas/hokkaido/ag0101'
  },
  miyagi: {
    name: '仙台',
    path: 'areas/miyagi/ag0401/sendai'
  },
  iwate: {
    name: '盛岡',
    path: 'areas/iwate/ag0300/morioka'
  },
  fukushima: {
    name: '郡山',
    path: 'areas/fukushima/ag0700/koriyama'
  },
  tokyo: [
    'ebisu',
    'shibuya',
    'shinjuku',
    'ikebukuro',
    'yurakucho',
    'kagurazaka',
    'ginza',
    'kichijoji',
    'meguro',
    'akasaka',
    'aoyama',
    'roppongi',
    'shinagawa',
    'nakameguro',
    'marunouchi',
    'asakusa',
    'jiyugaoka',
    'daikanyama',
    'nihonbashi',
    'yaesu',
    'omotesando',
    'iidabashi'
  ],
  kanagawa: [
    {
      name: '横浜',
      path: 'areas/kanagawa/ag1401'
    },
    'kawasaki',
    'sagamihara'
  ],
  saitama: [
    {
      name: 'さいたま',
      path: 'areas/saitama/ag1101'
    },
    'omiya',
    'urawa',
    'kawagoe'
  ],
  gunma: ['takasaki'],
  ibaraki: ['mito'],
  ishikawa: ['kanazawa'],
  nagano: ['matsumoto'],
  aichi: [
    'sakae',
    'toyohashi',
    {
      name: '名古屋',
      path: 'areas/aichi/ag2301'
    }
  ],
  shizuoka: ['hamamatsu'],
  osaka: ['umeda', 'shinsaibashi', 'tennoji'],
  hyogo: [
    'sannomiya',
    'himeji',
    {
      name: '神戸',
      path: 'areas/hyogo/ag2801'
    }
  ],
  hiroshima: ['fukuyama'],
  ehime: ['matsuyama'],
  fukuoka: ['kitakyushu', 'hakata', 'tenjin']
}
const MACHICON_DEFAULT_CATEGORY = [
  {
    title: '全国の地域から街コン（全イベント）を探す',
    suffixPath: '/',
    type: 'machicon'
  }
]
const KONKATSU_DEFAULT_CATEGORY = [
  {
    title: '全国の地域から婚活パーティー・お見合いパーティーを探す',
    suffixPath: '/categories/konkatsu',
    type: 'konkatsu'
  }
]
const KONKATSU_RANKING_CATEGORY = {
  title: '全国の人気ランキングから婚活パーティー・お見合いパーティーを探す',
  prefixPath: 'prefecture_rankings/',
  suffixPath: '/categories/konkatsu',
  type: 'ranking_konkatsu'
}

const isOpen = ref(false)

const store = useMainStore()
const route = useRoute()

watch(
  () => route.path,
  () => {
    isOpen.value = false
  }
)

const displayRegionSlugs = ref([['hokkaido', 'tohoku'], 'kanto', 'hokuriku', 'tokai', 'kinki', ['chugoku', 'shikoku'], 'kyushu', 'other'])
const categories: ComputedRef<Category[]> = computed(() => {
  const pageDetector = new PageDetector(route)

  if (pageDetector.isKonkatsuEventsPages) {
    return store.isSP ? KONKATSU_DEFAULT_CATEGORY.concat(KONKATSU_RANKING_CATEGORY) : KONKATSU_DEFAULT_CATEGORY
  }

  return MACHICON_DEFAULT_CATEGORY.concat(KONKATSU_DEFAULT_CATEGORY)
})

interface Category {
  title: string
  suffixPath: string
  prefixPath?: string
  type: string
}

const eventCategoryLinks = computed(() => {
  const globalCookie = new GlobalCookie(store)
  const pageDetector = new PageDetector(route)

  const isKonkatsuFirstTimeAccess = globalCookie.firstTimeAccessPage && pageDetector.isKonkatsuEventsPages
  const isAreaPage = pageDetector.isAreasDetailPage || pageDetector.isSearchWithPrefOrAreaPage

  const isFromEventList = pageDetector.isSearchPage && !isKonkatsuFirstTimeAccess && !pageDetector.isCategoryPage && !isAreaPage
  const fromTagList = isFromEventList ? '?from_event-list' : ''

  return [
    { name: '婚活パーティー', url: `/categories/konkatsu/${fromTagList}` },
    { name: '恋活パーティー', url: `/categories/party/${fromTagList}` },
    { name: '趣味コン', url: `/interests/${fromTagList}` },
    { name: '友達作り', url: `/features/tomokatsu/${fromTagList}`, note: '（社会人サークル/オフ会）' },
    { name: '自分磨きセミナー', url: `/categories/jibunmigaki/${fromTagList}` },
    { name: '婚活無料相談', url: `/features/counseling/${fromTagList}` }
  ]
})

function toArray<T>(obj: T | T[]) {
  if (!obj) return []
  return obj instanceof Array ? obj : [obj]
}

interface Region {
  id: number
  name: string
  slug: string
  path: string
}

interface Prefecture {
  id: number
  name: string
  slug: PrefectureSlug
  region_slug: string
  path: string
}

function regionBySlug(slug: string) {
  const listRegion: Region[] = addPathToListPlace(listRegions(), 'region')
  const region = listRegion.find(v => v.slug === slug)
  const listPrefectures: Prefecture[] = addPathToListPlace(getListPrefectureByRegionSlug(region?.slug || 'other'), 'prefecture')
  const prefectures = listPrefectures.map(pref => ({ name: pref.name.replace(/(県|都|府)$/, ''), path: pref.path, slug: pref.slug }))
  return {
    name: region?.name || 'undefined',
    path: region?.path || '',
    prefectures
  }
}

function prefecturesByRegionSlug(slug: string | string[]) {
  if (slug instanceof Array) return [...regionBySlug(slug[0]).prefectures, ...regionBySlug(slug[1]).prefectures]
  return regionBySlug(slug).prefectures
}

type PrefectureSlug = keyof typeof DISPLAY_AREAS
function areasByPrefecture(slug: PrefectureSlug) {
  const areas = DISPLAY_AREAS[slug]
  return toArray(areas).map(area => {
    if (typeof area === 'object') return area
    const { name, path } = addPathToPlace(findAreaBySlug(area), 'area') || { name: area, path: '' }
    return { name, path }
  })
}
</script>

<style lang="scss" scoped>
nav {
  position: relative;
  overflow-y: hidden;
  box-sizing: border-box;

  border-bottom: 1px solid $gray-c6;
  border-top: 1px solid $gray-c6;

  display: flex;
  flex-direction: column;
  gap: 1.6rem;

  align-items: center;

  section {
    width: 100%;
    max-width: 960px;

    &:first-child {
      .title {
        cursor: pointer;
      }

      .category {
        display: none;
      }
    }

    &:not(:first-child) {
      display: none;
    }

    .title {
      position: relative;
      align-content: center;
      margin-bottom: 0;
      padding: 1.2rem 1rem 1.2rem 0.7rem;
      background: $white;
      color: $black-1a;

      @include sp {
        padding: 1.2rem 3.6rem 1.2rem 1rem;
      }
    }
  }

  &.open {
    section {
      &:first-child {
        .category {
          display: flex;
        }
      }

      &:not(:first-child) {
        display: revert;
      }
    }
  }
}

a {
  display: inline-block;
  color: $black-1a;

  &:hover {
    text-decoration: underline;
  }
}

.disabledUrl {
  pointer-events: none;
}

.category {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  padding: 0 16rem 0rem 1rem;

  @include sp {
    padding-right: 1rem;
    gap: 1.6rem;
  }
}

.region-list {
  display: flex;
  justify-content: left;
  font-size: 1.2rem;
  gap: 3rem;

  @include sp {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
}

.region {
  flex: 0 0 10%;
  width: 10%;

  @include sp {
    flex: 0 0 100%;
    max-width: 100%;
  }

  > :not(:first-child)::before {
    content: '・';
    display: inline-block;
    text-decoration: none;
  }
}

.prefecture {
  display: inline;

  &:not(:first-child)::before {
    content: ' | ';
  }

  a {
    color: $gray-55;
  }
}

.areas-list {
  display: inline;
}

.area {
  display: inline-block;

  &:first-child::before {
    content: '（';
  }

  &:last-child::after {
    content: '）';
  }

  &:not(:last-child) {
    margin-right: 1rem;
  }

  > a {
    color: $gray-55;
  }
}

.toggle-icon {
  position: absolute;
  right: 0;
  top: 50%;
  transform: translate(-16px, -50%) rotate(0);

  &.open {
    transform: translate(-16px, -50%) rotate(-180deg);
  }
}

.categories-list {
  padding: 0 16rem 1.2rem 1rem;
  font-size: 1.2rem;

  @include sp {
    padding-right: 1rem;
  }

  > li {
    display: inline;

    &:not(:first-child)::before {
      content: ' | ';
    }

    a {
      color: $gray-55;
    }
  }
}
</style>
